import { message } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { isEmpty } from 'lodash';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import {
  IntegrationProvider,
  JiraConfig,
  JiraIssueType,
  MappingMechanism,
  ProjectMappingMechanism,
} from 'api/integrations';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import {
  getJiraFormValues,
  TableLoader,
} from 'components/molecules/integrations-commons';
import { setDirtyState } from 'data/templates';
import { useProjectTemplateTask } from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import {
  useQueryGetJiraFields,
  useQueryGetJiraProjects,
  useQueryJiraEpic,
  useQueryJiraIssueTypes,
} from 'queries/jira-integration';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import {
  useTemplateActiveTaskLinkedResource,
  useTemplateTaskCommonActions,
} from '../../../../commons';
import { JiraTemplatesFieldsForm } from './jira-templates-fields-form';
import { JiraTemplatesForm } from './jira-templates-form';

type JiraPane = {
  jiraConfig: JiraConfig;
  taskId: number;
};

const Card = styled.div`
  height: calc(100vh - 240px);
  padding: 32px;
  background: #fff;
  overflow: scroll;
  min-height: 50vh;
  width: 688px;
  margin: auto;
`;

const FormContainer = styled.div<{ disable?: boolean }>`
  ${ifProp(
    prop('disable', true),
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `,
  )};
`;

const CheckboxContainer = styled.div`
  margin-bottom: 32px;
`;

export const JiraPane: FC<JiraPane> = ({ jiraConfig, taskId }) => {
  const enableJiraText = useTranslation('templates.jira.enable_jira');
  const loadingText = useTranslation('templates.jira.enable_jira.loading');

  const {
    integrationId,
    settingsMeta: {
      fields,
      fixedProjectId,
      fixedEpicId: jiraFixedEpicId,
      epicMappingMechanism,
      projectMappingMechanism,
    },
  } = jiraConfig;

  const linkedResources = useTemplateActiveTaskLinkedResource([
    'task_id',
    taskId,
  ]);

  const savedJiraConfig = useMemo(
    () =>
      linkedResources?.find(
        ({ externalIntegrationType }) =>
          externalIntegrationType === IntegrationProvider.Jira,
      ),
    [linkedResources],
  );

  const taskData = useProjectTemplateTask(taskId);
  const { onJiraFormChange } = useTemplateTaskCommonActions(taskData);
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [fieldsForm] = Form.useForm();
  const [enableMapping, setEnableMapping] = useState(false);
  const [jiraProjectId, setJiraProjectId] = useState<string | null>(
    savedJiraConfig?.externalResourceInputs?.projectId ??
      fixedProjectId ??
      null,
  );
  const [jiraIssueTypeId, setJiraIssueTypeId] = useState<string | null>(
    savedJiraConfig?.externalResourceInputs?.issuetype ?? null,
  );
  const [jiraEpicId, setJiraEpicId] = useState<string | null>(
    savedJiraConfig?.externalResourceInputs?.epicId ?? jiraFixedEpicId ?? null,
  );

  const isFixedProjectMapping = useMemo(
    () => projectMappingMechanism === ProjectMappingMechanism.Fixed,
    [projectMappingMechanism],
  );

  const { data: epicsList, isLoading: loadingEpics } = useQueryJiraEpic(
    jiraConfig.integrationId,
    jiraProjectId!,
    {
      refetchOnWindowFocus: false,
      enabled: jiraProjectId != null,
      onSuccess: (epics) => {
        const selectedEpic = epics.find(({ id }) => id === jiraFixedEpicId);

        if (selectedEpic) {
          form.setFieldsValue({
            epicId: {
              value: selectedEpic.id,
              valueStr: selectedEpic.id,
              label: selectedEpic.name,
            },
          });
        }

        return true;
      },
    },
  );

  const { data: issueTypes, isLoading: loadingIssueTypes } =
    useQueryJiraIssueTypes(jiraConfig.integrationId, jiraProjectId!, {
      refetchOnWindowFocus: false,
      enabled: jiraProjectId != null,
      onSuccess: (res: JiraIssueType[]) => {
        const selectedIssueType = (res ?? []).find(
          ({ id }) => id === jiraIssueTypeId,
        );

        if (
          selectedIssueType != null &&
          savedJiraConfig?.externalResourceInputs?.issuetype == null &&
          isFixedProjectMapping
        ) {
          form.setFieldsValue({
            issuetype: {
              value: selectedIssueType.id,
              valueStr: selectedIssueType.id,
              label: selectedIssueType.name,
            },
          });
        }
      },
    });

  const { data: fieldsList, isLoading: loadingFields } = useQueryGetJiraFields(
    integrationId,
    jiraProjectId!,
    jiraIssueTypeId!,
    {
      enabled: jiraProjectId != null && jiraIssueTypeId != null,
      refetchOnWindowFocus: false,
    },
  );

  const { data: jiraProjects, isLoading: loadingJiraProjects } =
    useQueryGetJiraProjects(jiraConfig.integrationId, {
      enabled: Boolean(jiraConfig.integrationId),
    });

  const disableSave = () => {
    dispatch(
      setDirtyState({
        id: 0,
        _state: false,
      }),
    );
  };

  const onConnect = () => {
    queueMacrotask(async () => {
      try {
        const defaultValues = await form.validateFields();
        const jiraFields = await fieldsForm.validateFields();
        const filteredJiraFields = Object.keys(jiraFields)?.reduce(
          (sum, curr) => {
            if (jiraFields[curr] && jiraFields[curr] !== '') {
              return {
                ...sum,
                [curr]: jiraFields[curr],
              };
            }

            return sum;
          },
          {},
        );

        onJiraFormChange({
          taskId: taskData.taskId,
          data: {
            ...defaultValues,
            ...filteredJiraFields,
            projectId: defaultValues?.projectId?.value,
            issuetype: defaultValues?.issuetype?.value,
            epicId: defaultValues?.epicId?.value,
          },
        });

        // When only defaultValues changes
        if (isEmpty(jiraFields)) {
          disableSave();
        }
      } catch (err) {
        logger.warn('Templates / Jira / Form validation', { err });
        disableSave();
      }
    });
  };

  const onDisconnect = () => {
    onJiraFormChange({
      taskId: taskData.taskId,
      data: undefined,
    });
  };

  const onStatusToggle = (e: CheckboxChangeEvent) => {
    const {
      target: { checked },
    } = e;

    if (loadingFields || loadingEpics || loadingIssueTypes) {
      message.info(loadingText);

      return false;
    }

    if (checked) {
      onConnect();
    } else {
      onDisconnect();
    }

    setEnableMapping(checked);

    return true;
  };

  const selectedEpicMeta = useMemo(() => {
    const selected = epicsList?.find((epic) => epic.id === jiraEpicId);

    return {
      label: selected?.name,
      value: selected?.id,
      valueStr: selected?.id,
    };
  }, [epicsList, jiraEpicId]);

  const selectedIssueTypeMeta = useMemo(() => {
    const selected = issueTypes?.find(
      (issueType) => issueType.id === jiraIssueTypeId,
    );

    return {
      label: selected?.name,
      value: selected?.id,
      valueStr: selected?.id,
    };
  }, [jiraIssueTypeId, issueTypes]);

  const selectedProjectMeta = useMemo(() => {
    const selected = jiraProjects?.find(
      (jiraProject) => jiraProject.id === jiraProjectId,
    );

    return { label: selected?.name, value: selected?.id };
  }, [jiraProjectId, jiraProjects]);

  const formSetFieldsValueRef = useRef(form.setFieldsValue);

  useEffect(() => {
    formSetFieldsValueRef.current = form.setFieldsValue;
  }, [formSetFieldsValueRef, form]);

  const fieldsFormSetFieldsValueRef = useRef(fieldsForm.setFieldsValue);

  useEffect(() => {
    fieldsFormSetFieldsValueRef.current = fieldsForm.setFieldsValue;
  }, [fieldsFormSetFieldsValueRef, fieldsForm]);

  useEffect(() => {
    const setEpicValue =
      epicMappingMechanism === MappingMechanism.PromptDuringIssuecreation ||
      epicMappingMechanism === MappingMechanism.Fixed;

    if (savedJiraConfig != null) {
      formSetFieldsValueRef.current?.({
        ...savedJiraConfig?.externalResourceInputs,
        epicId: setEpicValue ? selectedEpicMeta : undefined,
        issuetype: selectedIssueTypeMeta,
        projectId: selectedProjectMeta, // set project Id by default, its a SELECT Option type
      });
      fieldsFormSetFieldsValueRef.current?.({
        ...savedJiraConfig?.externalResourceInputs,
      });
      setEnableMapping(true);
    } else if (isFixedProjectMapping) {
      formSetFieldsValueRef.current?.({
        ...getJiraFormValues(fields),
        summary: taskData?.taskName,
        description: taskData?.taskDescription ?? '',
        projectId: selectedProjectMeta,
      });
      fieldsFormSetFieldsValueRef.current?.({
        ...getJiraFormValues(fields),
      });
    } else {
      formSetFieldsValueRef.current?.({
        summary: taskData?.taskName,
        description: taskData?.taskDescription ?? '',
        projectId: selectedProjectMeta,
      });
    }
  }, [
    fieldsFormSetFieldsValueRef,
    isFixedProjectMapping,
    formSetFieldsValueRef,
    setEnableMapping,
    selectedEpicMeta,
    fields,
    taskData,
    epicMappingMechanism,
    selectedIssueTypeMeta,
    savedJiraConfig,
    selectedProjectMeta,
    jiraProjects,
  ]);

  const handleProjectChange = async (projectId: string) => {
    await setJiraProjectId(projectId);
    await setJiraIssueTypeId(null);
    await setJiraEpicId(null);
    await fieldsForm.resetFields();
    onConnect();
  };

  const handleIssueTypeChange = async (issueTypeId: string) => {
    await setJiraIssueTypeId(issueTypeId);
    await fieldsForm.resetFields();
    onConnect();
  };

  const handleEpicChange = async (epicId: string) => {
    await setJiraEpicId(epicId);
    onConnect();
  };

  return (
    <Card>
      <CheckboxContainer>
        <Checkbox
          checked={enableMapping}
          onChange={onStatusToggle}
          disabled={loadingFields || loadingEpics || loadingIssueTypes}
        >
          {enableJiraText}
        </Checkbox>
      </CheckboxContainer>

      <FormContainer disable={!enableMapping}>
        {loadingFields &&
        loadingEpics &&
        loadingIssueTypes &&
        loadingJiraProjects ? (
          <PageLoading />
        ) : (
          <>
            <JiraTemplatesForm
              jiraConfig={jiraConfig}
              form={form}
              onChange={onConnect}
              onProjectChange={handleProjectChange}
              onIssueTypeChange={handleIssueTypeChange}
              onEpicChange={handleEpicChange}
              issueTypes={issueTypes}
              loadingIssueTypes={loadingIssueTypes}
              epicsList={epicsList}
              loadingEpicsList={loadingEpics}
              jiraProjects={jiraProjects}
              loadingJiraProjects={loadingJiraProjects}
              disabled={!enableMapping}
            />
            {loadingFields ? (
              <TableLoader />
            ) : (
              <JiraTemplatesFieldsForm
                form={fieldsForm}
                fieldsList={fieldsList}
                onChange={onConnect}
              />
            )}
          </>
        )}
      </FormContainer>
    </Card>
  );
};
